
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, Users, MessageCircle, Plus, Settings, Eye, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { BedListing, Referral } from '@/types/listings';
import { AssistedLivingScorecard } from '@/types/scorecard';
import ListingForm from '@/components/listings/ListingForm';
import ReferralStatusModal from '@/components/referrals/ReferralStatusModal';
import EmailExchangeViewer from '@/components/referrals/EmailExchangeViewer';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import Navigation from '@/components/Navigation';

const ProviderDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const { toast } = useToast();
  const [listings, setListings] = useState<BedListing[]>([]);
  const [selectedListing, setSelectedListing] = useState<BedListing | null>(null);
  const [showListingForm, setShowListingForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [showReferralStatusModal, setShowReferralStatusModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scorecardData, setScorecardData] = useState<AssistedLivingScorecard | null>(null);

  const getListingLimit = () => {
    // This would typically come from the subscription data
    // For now, we'll use a basic check
    return profile?.subscription_status === 'active' ? 2 : 0;
  };

  // Check subscription status
  const hasActiveSubscription = profile?.subscription_status === 'active';
  const canCreateListing = hasActiveSubscription && listings.length < getListingLimit();

  useEffect(() => {
    if (user) {
      fetchProviderData();
    }
  }, [user]);

  const fetchProviderData = async () => {
    if (!user || !profile) return;

    try {
      // For HSS providers, fetch HSS service referrals; for regular providers, fetch housing referrals
      if (profile.role === 'hss_provider') {
        // Fetch HSS service referrals for this provider's county
        const { data: referralsData, error: referralsError } = await supabase
          .from('referrals')
          .select('*')
          .eq('referral_type', 'hss_service')
          .in('county', profile.counties_served || [])
          .order('created_at', { ascending: false });

        if (referralsError) throw referralsError;
        setReferrals((referralsData || []) as any[]);
        
        // HSS providers don't have traditional listings, so set empty array
        setListings([]);
      } else {
        // Fetch listings for regular providers
        const { data: listingsData, error: listingsError } = await supabase
          .from('bed_listings')
          .select('*')
          .eq('created_by', user.id)
          .order('created_at', { ascending: false });

        if (listingsError) throw listingsError;
        setListings(listingsData || []);

        // Fetch referrals for this provider's listings
        const listingIds = listingsData?.map(l => l.id) || [];
        if (listingIds.length > 0) {
          const { data: referralsData, error: referralsError } = await supabase
            .from('referrals')
            .select('*')
            .in('linked_listing', listingIds)
            .eq('referral_type', 'housing_placement')
            .order('created_at', { ascending: false });

          if (referralsError) throw referralsError;
          setReferrals((referralsData || []) as any[]);
        }
      }

      // Fetch scorecard data if NPI exists (only for regular providers)
      if (profile.npi && profile.role === 'provider') {
        const { data: scorecardData, error: scorecardError } = await supabase
          .from('assisted_living_scorecards')
          .select('*')
          .eq('npi', profile.npi)
          .maybeSingle();

        if (scorecardError) {
          console.warn('Error fetching scorecard data:', scorecardError);
        } else {
          setScorecardData(scorecardData);
        }
      }
    } catch (error) {
      console.error('Error fetching provider data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleListingCreated = () => {
    if (!canCreateListing) {
      toast({
        title: 'Subscription Required',
        description: 'Please upgrade your subscription to create listings.',
        variant: 'destructive',
      });
      return;
    }
    setShowListingForm(false);
    fetchProviderData();
  };

  const handleReferralUpdate = async (referralId: string, status: string, notes?: string) => {
    try {
      const { error } = await supabase
        .from('referrals')
        .update({ 
          status, 
          provider_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', referralId);

      if (error) throw error;

      // Send status change notification to the case manager who submitted the referral
      const referral = referrals.find(r => r.id === referralId);
      if (referral) {
        const { data: caseManager } = await supabase
          .from('profiles')
          .select('email, name')
          .eq('user_id', referral.referring_user)
          .single();

        if (caseManager) {
          await supabase.functions.invoke('send-notification', {
            body: {
              type: 'referral_status_change',
              recipientEmail: caseManager.email,
              recipientUserId: referral.referring_user,
              data: {
                facilityName: listings.find(l => l.id === referral.linked_listing)?.facility_name || 'Unknown Facility',
                status: status,
                providerNotes: notes || '',
                dashboardUrl: `${window.location.origin}/dashboard`,
                entityId: referralId,
                entityType: "referral"
              }
            }
          });
        }
      }

      toast({
        title: 'Success',
        description: 'Referral updated successfully',
      });

      fetchProviderData();
    } catch (error) {
      console.error('Error updating referral:', error);
      toast({
        title: 'Error',
        description: 'Failed to update referral',
        variant: 'destructive',
      });
    }
  };

  const handleViewScorecard = () => {
    if (!profile?.npi && !scorecardData) {
      toast({
        title: 'Information Needed',
        description: 'Please add your NPI to your profile to verify your facility on the MN Report Card.',
        variant: 'destructive',
      });
      return;
    }

    let url = 'https://www.health.state.mn.us/facilities/regulation/assistedliving/reportcard.html';
    
    if (scorecardData) {
      // Construct URL with facility-specific search parameters
      const searchParams = new URLSearchParams();
      if (scorecardData.facility_name) {
        searchParams.append('search', scorecardData.facility_name);
      }
      if (scorecardData.county) {
        searchParams.append('county', scorecardData.county);
      }
      if (scorecardData.npi) {
        searchParams.append('npi', scorecardData.npi);
      }
      
      if (searchParams.toString()) {
        url += `?${searchParams.toString()}`;
      }
    } else if (profile?.npi) {
      // Fallback to NPI search if scorecard data not found
      url += `?search=${encodeURIComponent(profile.npi)}`;
    }

    window.open(url, '_blank');
    
    toast({
      title: 'Verification Tip',
      description: scorecardData 
        ? `Look for "${scorecardData.facility_name}" to verify the scorecard matches your facility.`
        : 'Search for your facility using your NPI or facility name to verify the scorecard information.',
    });
  };

  const totalBeds = listings.reduce((sum, listing) => sum + listing.total_beds, 0);
  const availableBeds = listings.reduce((sum, listing) => sum + listing.beds_available, 0);
  const pendingReferrals = referrals.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Different UI for HSS providers vs regular providers
  if (profile?.role === 'hss_provider') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
        <Navigation />
        <header className="bg-white border-b border-border/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">HSS Provider Dashboard</h1>
              <p className="text-muted-foreground">Welcome back, {profile?.agency_name || profile?.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/profile'}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button variant="outline" size="sm" onClick={signOut}>
                Sign Out
              </Button>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-6 py-8">
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Referral Status</CardTitle>
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {profile?.accepting_clients ? 'Accepting' : 'Not Accepting'}
                </div>
                <p className="text-xs text-muted-foreground">Current status</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Referrals</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingReferrals}</div>
                <p className="text-xs text-muted-foreground">Requires response</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Counties Served</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{profile?.counties_served?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Service areas</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Referral Acceptance</CardTitle>
                <CardDescription>
                  Manage whether you're currently accepting new referrals
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium">Current Status</h4>
                      <p className="text-sm text-muted-foreground">
                        You are currently {profile?.accepting_clients ? 'accepting' : 'not accepting'} referrals
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={profile?.accepting_clients ? "default" : "secondary"}>
                        {profile?.accepting_clients ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button
                        size="sm"
                        variant={profile?.accepting_clients ? "outline" : "default"}
                        onClick={async () => {
                          try {
                            const { error } = await supabase
                              .from('profiles')
                              .update({ accepting_clients: !profile?.accepting_clients })
                              .eq('user_id', user.id);

                            if (error) throw error;

                            toast({
                              title: 'Status Updated',
                              description: `You are now ${!profile?.accepting_clients ? 'accepting' : 'not accepting'} referrals`,
                            });

                            // Refresh the page to update the status
                            window.location.reload();
                          } catch (error) {
                            console.error('Error updating status:', error);
                            toast({
                              title: 'Error',
                              description: 'Failed to update status',
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        {profile?.accepting_clients ? 'Stop Accepting' : 'Start Accepting'}
                      </Button>
                    </div>
                  </div>
                  
                  {profile?.bio && (
                    <div className="p-4 bg-muted rounded-lg">
                      <h5 className="font-medium mb-2">About Our Services</h5>
                      <p className="text-sm text-muted-foreground">{profile.bio}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Referrals</CardTitle>
                <CardDescription>
                  Review and respond to HSS service requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {referrals.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      No referrals yet.
                    </p>
                  ) : (
                    referrals.slice(0, 5).map(referral => (
                      <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium">
                            {referral.client_gender && referral.client_age 
                              ? `${referral.client_gender}, ${referral.client_age} years old`
                              : 'Client Details'
                            }
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {referral.housing_situation} • {referral.county}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Submitted {formatDistanceToNow(new Date(referral.created_at))} ago
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={
                            referral.status === 'pending' ? 'default' :
                            referral.status === 'accepted' ? 'default' :
                            referral.status === 'declined' ? 'destructive' : 'secondary'
                          }>
                            {referral.status}
                          </Badge>
                          {referral.status === 'pending' && (
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                onClick={() => handleReferralUpdate(referral.id, 'accepted')}
                              >
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReferralUpdate(referral.id, 'declined')}
                              >
                                Decline
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Regular provider dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Navigation />
      <header className="bg-white border-b border-border/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Provider Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {profile?.name || user?.email}</p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant={hasActiveSubscription ? "default" : "destructive"}>
              {profile?.subscription_status || 'inactive'}
            </Badge>
            {!hasActiveSubscription && (
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/subscription'}>
                Upgrade Plan
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleViewScorecard}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {scorecardData ? 'Verify Your Facility' : 'View MN Report Card'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/profile'}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Listings</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{listings.length}</div>
              <p className="text-xs text-muted-foreground">{availableBeds} available beds</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Referrals</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingReferrals}</div>
              <p className="text-xs text-muted-foreground">Requires response</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {listings.reduce((sum, listing) => sum + listing.clicks, 0)}
              </div>
              <p className="text-xs text-muted-foreground">Last 60 days</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Bed Listings</CardTitle>
                <Dialog open={showListingForm} onOpenChange={setShowListingForm}>
                  <DialogTrigger asChild>
                    <Button disabled={!canCreateListing}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Listing
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Listing</DialogTitle>
                    </DialogHeader>
                    <ListingForm onSuccess={handleListingCreated} />
                  </DialogContent>
                </Dialog>
              </div>
              <CardDescription>
                Manage your facility's availability and care offerings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {listings.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No listings yet. Create your first listing to get started.
                  </p>
                ) : (
                  listings.map(listing => (
                    <div key={listing.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium">{listing.facility_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {listing.beds_available} of {listing.total_beds} beds available
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {listing.clicks} clicks • Updated {formatDistanceToNow(new Date(listing.last_updated))} ago
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={listing.beds_available > 0 ? "default" : "secondary"}>
                          {listing.beds_available > 0 ? "Available" : "Full"}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Referrals</CardTitle>
              <CardDescription>
                Review and respond to placement requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {referrals.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No referrals yet.
                  </p>
                ) : (
                  referrals.slice(0, 5).map(referral => (
                    <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium">
                          {referral.client_gender}, {referral.client_age} years old
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {referral.waiver_type} • {referral.timeline}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Submitted {formatDistanceToNow(new Date(referral.created_at))} ago
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={
                          referral.status === 'pending' ? 'default' :
                          referral.status === 'accepted' ? 'default' :
                          referral.status === 'declined' ? 'destructive' : 'secondary'
                        }>
                          {referral.status}
                        </Badge>
                        {referral.status === 'pending' && (
                          <div className="flex space-x-1">
                            <Button
                              size="sm"
                              onClick={() => handleReferralUpdate(referral.id, 'accepted')}
                            >
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReferralUpdate(referral.id, 'declined')}
                            >
                              Decline
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProviderDashboard;
